import { useApolloClient } from '@apollo/client';
import { Form, message } from 'antd';
import { Formik } from 'formik';
import { pick } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type BankModuleSpecsFragment } from '../../../../api/fragments/BankModuleSpecs';
import {
    UpdateBankModuleDocument,
    type UpdateBankModuleMutation,
    type UpdateBankModuleMutationVariables,
} from '../../../../api/mutations/updateBankModule';
import { type BankModuleSettings } from '../../../../api/types';
import InputField from '../../../../components/fields/InputField';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';

export type BankModuleUpdateFormProps = {
    module: BankModuleSpecsFragment;
    onComplete: () => void;
};

type FormValues = BankModuleSettings;

const BankModuleUpdateForm = ({ module, onComplete }: BankModuleUpdateFormProps) => {
    const { t } = useTranslation(['bankModuleDetails']);
    const apolloClient = useApolloClient();

    const moduleId = module.id;

    const onSubmit = useHandleError(
        async (values: FormValues) => {
            message.loading({
                content: t('bankModuleDetails:updateDrawer.messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient
                .mutate<UpdateBankModuleMutation, UpdateBankModuleMutationVariables>({
                    mutation: UpdateBankModuleDocument,
                    variables: { moduleId, settings: values },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            message.success({
                content: t('bankModuleDetails:updateDrawer.messages.updateSuccess'),
                key: 'primary',
            });

            onComplete();
        },
        [onComplete, apolloClient, moduleId, t]
    );

    const initialValues = useMemo(
        (): FormValues => ({
            ...pick(['displayName'], module),
        }),
        [module]
    );

    const validate = useValidator(validators.requiredNonEmptyString('displayName'));

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Form id="updateBankModule" layout="vertical" name="updateBankModule" onSubmitCapture={handleSubmit}>
                    <InputField
                        {...t('bankModuleDetails:updateDrawer.fields.displayName', {
                            returnObjects: true,
                        })}
                        name="displayName"
                        required
                    />
                </Form>
            )}
        </Formik>
    );
};

export default BankModuleUpdateForm;
